/**
 * Utility functions for environment-specific behavior
 */

/**
 * Check if the current environment is development
 * @returns boolean indicating if we're in development environment
 */
export const isDevelopment = (): boolean => {
	return process.env.ENVIRONMENT_NAME !== 'prod' && process.env.ENVIRONMENT_NAME !== 'production';
};

/**
 * Get the filename from a stack trace
 * @returns The filename or 'unknown' if not found
 */
const getCallerFile = (): string => {
	const error = new Error();
	const stack = error.stack || '';
	const stackLines = stack.split('\n');

	// Find the first line that's not from this file
	for (let i = 2; i < stackLines.length; i++) {
		const match = stackLines[i].match(/\s+at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/i);
		if (match && !match[2].includes('environment.ts')) {
			// Extract just the filename without the path
			const fullPath = match[2];
			const pathParts = fullPath.split('/');
			return pathParts[pathParts.length - 1];
		}
	}

	return 'unknown';
};

/**
 * Conditionally log to console only in development environment
 * @param args - Arguments to pass to console.log
 */
export const devLog = (...args: any[]): void => {
	if (isDevelopment()) {
		const caller = getCallerFile();
		console.log(`%c[${caller}]`, 'color: #4CAF50; font-weight: bold;', ...args);
	}
};

/**
 * Conditionally log errors to console only in development environment
 * @param args - Arguments to pass to console.error
 */
export const devError = (...args: any[]): void => {
	if (isDevelopment()) {
		const caller = getCallerFile();
		console.error(`%c[${caller}]`, 'color: #F44336; font-weight: bold;', ...args);
	}
};

/**
 * Conditionally log warnings to console only in development environment
 * @param args - Arguments to pass to console.warn
 */
export const devWarn = (...args: any[]): void => {
	if (isDevelopment()) {
		const caller = getCallerFile();
		console.warn(`%c[${caller}]`, 'color: #FF9800; font-weight: bold;', ...args);
	}
};
